import type { PrimitiveLabels } from '@shared/tracking/TrackingLabels.ts';
import type { DresonRuleResolveEvent, Page } from '@shared/types/Tilelist.ts';

export type NavigationCheckpoint = {
    rule: string;
    entryRule: string;
    page: Page;
    tsLink?: PrimitiveLabels;
    historic: true;
};

export function createNavigationCheckpoint(
    rule: string,
    entryRule: string,
    page: Page,
    tsLink?: DresonRuleResolveEvent['tsLink'],
): NavigationCheckpoint {
    return { rule, entryRule, page: { ...page }, tsLink, historic: true };
}

function isLabelValue(value: unknown): boolean {
    const isScalar = (item: unknown) =>
        typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean';
    return isScalar(value) || (Array.isArray(value) && value.every(isScalar));
}

function isPrimitiveLabels(value: unknown): value is PrimitiveLabels {
    return (
        typeof value === 'object' && value !== null && !Array.isArray(value) && Object.values(value).every(isLabelValue)
    );
}

function isPage(value: unknown): value is Page {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
    return (
        Object.keys(value).every((key) => ['sortiertnach', 'l', 'o', 'c'].includes(key)) &&
        (!('sortiertnach' in value) || typeof value.sortiertnach === 'string') &&
        (!('l' in value) || typeof value.l === 'string') &&
        (!('o' in value) || typeof value.o === 'string') &&
        (!('c' in value) || typeof value.c === 'string')
    );
}

export function isNavigationCheckpoint(value: unknown): value is NavigationCheckpoint {
    if (typeof value !== 'object' || value === null) return false;
    return (
        'rule' in value &&
        typeof value.rule === 'string' &&
        'entryRule' in value &&
        typeof value.entryRule === 'string' &&
        'page' in value &&
        isPage(value.page) &&
        'historic' in value &&
        value.historic === true &&
        (!('tsLink' in value) || value.tsLink === undefined || isPrimitiveLabels(value.tsLink))
    );
}

export function resolveEntryRule(state: unknown, fallback: string): string {
    return isNavigationCheckpoint(state) ? state.entryRule : fallback;
}
